import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, Calculator, Building2, Car, DollarSign, TrendingUp,
  CheckCircle, Clock, Loader, ArrowRight, Banknote, X, FileText,
  Bell, Plus, Phone, Zap, RefreshCw, AlertCircle, Users
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, BarChart, Bar
} from 'recharts';
import api from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';

const BL = '#0D2B6B', YW = '#F5C400', GR = '#059669';

/* ─── Stat card ─── */
function StatCard({ label, value, icon: Icon, color, sub, to, accent, pulse }) {
  const content = (
    <div className={`rounded-2xl p-4 border-2 transition-all hover:-translate-y-0.5 hover:shadow-lg relative overflow-hidden
      ${accent ? 'border-transparent text-white shadow-lg' : 'bg-white border-gray-100'}`}
      style={accent ? { background: `linear-gradient(135deg, ${BL}, #1a3f8f)` } : {}}>
      {pulse && <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 rounded-full animate-ping" />}
      <div className="flex justify-between items-start mb-3">
        <p className={`text-[10px] font-bold uppercase tracking-wider ${accent ? 'text-white/60' : 'text-gray-400'}`}>{label}</p>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: accent ? 'rgba(255,255,255,0.15)' : color + '18' }}>
          <Icon className="w-[18px] h-[18px]" style={{ color: accent ? YW : color }} />
        </div>
      </div>
      <p className={`text-2xl font-black ${accent ? 'text-yellow-400' : 'text-gray-900'}`}>{value}</p>
      {sub && <p className={`text-[10px] mt-1 ${accent ? 'text-white/50' : 'text-gray-400'}`}>{sub}</p>}
    </div>
  );
  return to ? <Link to={to} className="block cursor-pointer">{content}</Link> : <div className="cursor-default">{content}</div>;
}

/* ─── Financial Drawer ─── */
function FinancialDrawer({ open, onClose, stats }) {
  if (!open) return null;
  const rev    = stats?.revenue      || 0;
  const spend  = stats?.car_spend    || 0;
  const gross  = stats?.gross_profit || 0;
  const gst    = Math.round(rev / 11);
  const margin = rev > 0 ? Math.round((gross / rev) * 100) : 0;
  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-sm bg-white z-50 shadow-2xl flex flex-col">
        <div className="text-white p-6 shrink-0" style={{ background: `linear-gradient(135deg, ${BL}, #1a3f8f)` }}>
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-white/60 text-xs font-bold uppercase tracking-wider">Financial Overview</p>
              <h2 className="text-2xl font-black mt-1">Revenue Summary</h2>
            </div>
            <button onClick={onClose} className="p-2 bg-white/10 rounded-lg"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3 bg-white/10 rounded-xl p-4">
            <div><p className="text-white/50 text-xs mb-1">REVENUE</p><p className="text-2xl font-black text-yellow-400">${rev.toLocaleString()}</p></div>
            <div><p className="text-white/50 text-xs mb-1">GROSS PROFIT</p><p className={`text-2xl font-black ${gross >= 0 ? 'text-green-400' : 'text-red-400'}`}>${gross.toLocaleString()}</p></div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {[
            ['Revenue Collected',     `$${rev.toLocaleString()}`,              `Paid towing jobs & invoices`,          'text-gray-900'],
            ['Car Purchase Spend',    `-$${spend.toLocaleString()}`,            `${stats?.car_purchases||0} vehicles bought`, 'text-red-600'],
            ['B2B Outstanding',       `$${(stats?.b2b_outstanding||0).toLocaleString()}`, 'Unpaid client jobs',         'text-amber-600'],
            ['GST Collected (est.)', `$${gst.toLocaleString()}`,               '1/11th of revenue (ATO)',              'text-indigo-600'],
          ].map(([l, v, s, c]) => (
            <div key={l} className="flex justify-between items-center py-4 border-b border-gray-100">
              <div><p className="font-semibold text-gray-800 text-sm">{l}</p><p className="text-xs text-gray-400 mt-0.5">{s}</p></div>
              <p className={`font-black text-lg ${c}`}>{v}</p>
            </div>
          ))}
          <div className={`rounded-xl p-4 mt-4 ${gross >= 0 ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
            <div className="flex justify-between items-center mb-2">
              <p className={`text-sm font-bold ${gross >= 0 ? 'text-green-800' : 'text-red-800'}`}>Gross Profit</p>
              <p className={`text-2xl font-black ${gross >= 0 ? 'text-green-700' : 'text-red-700'}`}>${gross.toLocaleString()}</p>
            </div>
            <div className="h-2.5 bg-white/60 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${gross >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                style={{ width: `${Math.min(Math.abs(margin), 100)}%` }} />
            </div>
            <p className="text-xs text-gray-500 mt-1.5">Profit margin: <strong>{margin}%</strong></p>
          </div>
          <Link to="/admin/invoices" onClick={onClose}
            className="mt-4 flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-white hover:opacity-90"
            style={{ backgroundColor: BL }}>
            <FileText className="w-4 h-4" /> View All Invoices
          </Link>
        </div>
      </div>
    </>
  );
}

/* ─── Horizontal bar chart ─── */
function HBarChart({ title, data, color, emptyMsg }) {
  if (!data || data.length === 0) return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 flex flex-col min-h-[200px]">
      <p className="font-bold text-gray-800 text-sm mb-1">{title}</p>
      <div className="flex-1 flex items-center justify-center text-gray-300 text-sm flex-col gap-2">
        <AlertCircle className="w-8 h-8 text-gray-200" />
        <span>{emptyMsg || 'No data yet'}</span>
      </div>
    </div>
  );
  const max = Math.max(...data.map(d => d.count || 0)) || 1;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <p className="font-bold text-gray-800 text-sm mb-4">{title}</p>
      <div className="space-y-2.5">
        {data.map((d, i) => {
          const label = d.suburb || d.area || d.make || '—';
          const val   = d.count  || 0;
          const pct   = Math.max((val / max) * 100, 6);
          return (
            <div key={i} className="flex items-center gap-3">
              <span className="text-xs text-gray-600 font-medium w-24 shrink-0 truncate text-right">{label}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                <div className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-700"
                  style={{ width: `${pct}%`, backgroundColor: color }}>
                  <span className="text-[10px] font-black text-white">{val}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const SC = {
  pending:     'bg-amber-100 text-amber-800',
  confirmed:   'bg-blue-100 text-blue-800',
  in_progress: 'bg-orange-100 text-orange-800',
  completed:   'bg-green-100 text-green-800',
  invoiced:    'bg-purple-100 text-purple-800',
  cancelled:   'bg-red-100 text-red-800',
};

/* ─── MAIN DASHBOARD ─── */
export default function Dashboard() {
  const { settings } = useSettings();
  const [stats,   setStats]   = useState(null);
  const [chart,   setChart]   = useState(null);
  const [recent,  setRecent]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFin, setShowFin] = useState(false);
  const [range,   setRange]   = useState(30);
  const [toggle,  setToggle]  = useState('revenue');

  const load = useCallback(async () => {
    try {
      const [s, ch, r] = await Promise.all([
        api.get('/api/dashboard/stats'),
        api.get('/api/dashboard/chart-data'),
        api.get('/api/dashboard/recent-bookings'),
      ]);
      setStats(s.data);
      setChart(ch.data);
      setRecent(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader className="w-10 h-10 animate-spin" style={{ color: BL }} />
    </div>
  );

  const dailySlice = (chart?.daily || []).slice(-range).map(d => ({
    ...d,
    label: d.date?.slice(5),
  }));

  const fmtCurrency = v => `$${(v || 0).toLocaleString()}`;
  const hasAlerts   = (stats?.pending || 0) > 0 || (stats?.new_estimates || 0) > 0;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5">
      <FinancialDrawer open={showFin} onClose={() => setShowFin(false)} stats={stats} />

      {/* ── Alert Bar ── */}
      {hasAlerts && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 flex flex-wrap gap-3 items-center">
          <Bell className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="font-bold text-amber-800 text-sm">Action Required:</p>
          {(stats?.pending || 0) > 0 && (
            <Link to="/admin/bookings"
              className="flex items-center gap-1.5 bg-amber-600 text-white text-xs font-bold px-3 py-1.5 rounded-full hover:bg-amber-700">
              <Calendar className="w-3.5 h-3.5" /> {stats.pending} pending booking{stats.pending > 1 ? 's' : ''} — confirm now
            </Link>
          )}
          {(stats?.new_estimates || 0) > 0 && (
            <Link to="/admin/estimates"
              className="flex items-center gap-1.5 bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-full hover:bg-blue-700">
              <Calculator className="w-3.5 h-3.5" /> {stats.new_estimates} new estimate{stats.new_estimates > 1 ? 's' : ''} — respond now
            </Link>
          )}
          {(stats?.b2b_outstanding || 0) > 0 && (
            <Link to="/admin/b2b"
              className="flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full hover:bg-orange-600">
              <Building2 className="w-3.5 h-3.5" /> ${(stats.b2b_outstanding || 0).toLocaleString()} B2B outstanding
            </Link>
          )}
        </div>
      )}

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black" style={{ color: BL }}>
            Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening'} 👋
          </h1>
          <p className="text-sm text-gray-400 mt-0.5">
            {settings.company_name || 'Apex Towing'} · {new Date().toLocaleDateString('en-AU', { weekday:'long', day:'numeric', month:'long' })} · Live data
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load}
            className="flex items-center gap-2 border-2 border-gray-200 text-gray-600 px-4 py-2 rounded-xl text-sm font-semibold hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" />
          </button>
          <Link to="/admin/bookings"
            className="flex items-center gap-2 text-white px-4 py-2 rounded-xl text-sm font-bold hover:opacity-90"
            style={{ backgroundColor: BL }}>
            <Plus className="w-4 h-4" /> New Booking
          </Link>
        </div>
      </div>

      {/* ── Quick Actions ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: Calendar,   label: 'New Booking',    sub: 'Schedule a job',      to: '/admin/bookings',    bg: BL,        col: 'white' },
          { icon: Calculator, label: 'New Estimate',   sub: 'Customer quote',      to: '/admin/estimates',   bg: '#7c3aed', col: 'white' },
          { icon: Building2,  label: 'Add B2B Job',    sub: 'Log client work',     to: '/admin/b2b',         bg: GR,        col: 'white' },
          { icon: Car,        label: 'Buy a Car',      sub: 'Record purchase',     to: '/admin/car-purchases', bg: '#f59e0b', col: 'white' },
        ].map(({ icon: Icon, label, sub, to, bg, col }) => (
          <Link key={label} to={to}
            className="flex items-center gap-3 p-4 rounded-2xl border-2 border-transparent hover:opacity-90 transition-all hover:-translate-y-0.5 shadow-md"
            style={{ backgroundColor: bg, color: col }}>
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5" style={{ color: col }} />
            </div>
            <div>
              <p className="font-black text-sm leading-none">{label}</p>
              <p className="text-[11px] opacity-60 mt-0.5">{sub}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* ── Today at a Glance ── */}
      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Today at a Glance</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Today's Jobs"   value={stats?.today_bookings ?? 0} icon={Calendar}    color="#3b82f6" sub="Scheduled today"   to="/admin/bookings" pulse={(stats?.today_bookings || 0) > 0} />
          <StatCard label="Pending"        value={stats?.pending        ?? 0} icon={Clock}       color="#f59e0b" sub="Need confirmation"  to="/admin/bookings" pulse={(stats?.pending || 0) > 0} />
          <StatCard label="In Progress"    value={stats?.confirmed      ?? 0} icon={Zap}         color="#0891b2" sub="Confirmed & ready"  to="/admin/bookings" />
          <StatCard label="New Estimates"  value={stats?.new_estimates  ?? 0} icon={Calculator}  color="#8b5cf6" sub="Awaiting response"  to="/admin/estimates" pulse={(stats?.new_estimates || 0) > 0} />
        </div>
      </div>

      {/* ── All Time Operations ── */}
      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">All Time — Operations</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Completed Jobs" value={stats?.completed      ?? 0} icon={CheckCircle} color={GR}      sub="Successfully done"  to="/admin/bookings" />
          <StatCard label="Invoiced"       value={stats?.invoiced       ?? 0} icon={FileText}    color="#7c3aed" sub="Invoice generated"   to="/admin/invoices" />
          <StatCard label="Cars Purchased" value={stats?.car_purchases  ?? 0} icon={Car}         color="#6366f1" sub="In database"         to="/admin/car-purchases" />
          <StatCard label="Total Bookings" value={stats?.total_bookings ?? 0} icon={Calendar}    color={BL}      sub="All time"            to="/admin/bookings" />
        </div>
      </div>

      {/* ── Financials ── */}
      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Financials</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div onClick={() => setShowFin(true)} className="md:col-span-2 cursor-pointer">
            <StatCard label="Total Revenue" value={fmtCurrency(stats?.revenue)} icon={DollarSign} color={GR} sub="Click for full breakdown — paid invoices" accent />
          </div>
          <div onClick={() => setShowFin(true)} className="cursor-pointer">
            <StatCard label="Gross Profit"    value={fmtCurrency(stats?.gross_profit)}  icon={TrendingUp} color={GR}      sub="Revenue minus car spend" />
          </div>
          <StatCard label="B2B Outstanding" value={fmtCurrency(stats?.b2b_outstanding)} icon={Building2}  color="#ef4444" sub="Unpaid client jobs"  to="/admin/b2b" />
        </div>
        {/* GST + Financial bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <div className="bg-indigo-50 border-2 border-indigo-100 rounded-2xl p-4 flex justify-between items-center">
            <div>
              <p className="text-xs font-bold text-indigo-600 uppercase tracking-wide">GST Payable (Est.)</p>
              <p className="text-2xl font-black text-indigo-700 mt-1">{fmtCurrency(Math.round((stats?.revenue||0)/11))}</p>
              <p className="text-xs text-indigo-400 mt-0.5">Approx. ATO obligation</p>
            </div>
            <Banknote className="w-10 h-10 text-indigo-200" />
          </div>
          <div className="md:col-span-2 bg-white border border-gray-200 rounded-2xl p-4">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Revenue vs Car Spend</p>
            <div className="flex gap-2 h-5 rounded-full overflow-hidden bg-gray-100">
              {(stats?.revenue || 0) > 0 ? (
                <>
                  <div className="h-full rounded-l-full flex items-center justify-center"
                    style={{ width:`${Math.round(((stats?.revenue||0)-(stats?.car_spend||0))/(stats?.revenue||1)*100)}%`, backgroundColor: GR }}>
                    <span className="text-[9px] font-black text-white px-1">Profit</span>
                  </div>
                  {(stats?.car_spend || 0) > 0 && (
                    <div className="h-full flex-1 rounded-r-full flex items-center justify-center" style={{ backgroundColor: '#ef4444' }}>
                      <span className="text-[9px] font-black text-white px-1">Cars</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="h-full w-full flex items-center justify-center text-[10px] text-gray-400">No revenue data yet</div>
              )}
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>Profit: <strong className="text-green-600">{stats?.revenue > 0 ? Math.round(((stats?.revenue||0)-(stats?.car_spend||0))/(stats?.revenue)*100) : 0}%</strong></span>
              <span>Car spend: <strong className="text-red-500">{stats?.revenue > 0 ? Math.round((stats?.car_spend||0)/(stats?.revenue)*100) : 0}%</strong></span>
              <span>Total jobs: <strong style={{ color: BL }}>{stats?.total_bookings || 0}</strong></span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Revenue Chart + Summary ── */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <h2 className="font-bold text-gray-900">Revenue Trend</h2>
              <p className="text-xs text-gray-400 mt-0.5">Daily performance history</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <div className="flex bg-gray-100 rounded-xl p-0.5 text-xs font-bold">
                {[[14,'2W'],[30,'1M'],[90,'3M']].map(([v,l]) => (
                  <button key={v} onClick={() => setRange(v)}
                    className={`px-3 py-1.5 rounded-lg transition-all ${range===v?'bg-white shadow text-gray-900':'text-gray-400 hover:text-gray-600'}`}>{l}</button>
                ))}
              </div>
              <div className="flex bg-gray-100 rounded-xl p-0.5 text-xs font-bold">
                {[['revenue','Revenue'],['car_spend','Car $'],['jobs','Jobs']].map(([v,l]) => (
                  <button key={v} onClick={() => setToggle(v)}
                    className={`px-3 py-1.5 rounded-lg transition-all ${toggle===v?'bg-white shadow text-gray-900':'text-gray-400 hover:text-gray-600'}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
          {dailySlice.some(d => (d.revenue||0) > 0 || (d.jobs||0) > 0) ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={dailySlice} margin={{ top:5, right:10, left:-10, bottom:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize:10 }} tickLine={false} axisLine={false}
                  tickFormatter={v => { const [m,d] = v.split('-'); return `${parseInt(d)} ${['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m)]}`; }} />
                <YAxis tick={{ fontSize:10 }} tickLine={false} axisLine={false}
                  tickFormatter={v => v>=1000 ? `$${(v/1000).toFixed(0)}k` : v>0 ? `$${v}` : String(v)} />
                <Tooltip
                  formatter={(val, name) => [name==='jobs' ? val : `$${val.toLocaleString()}`,
                    name==='jobs' ? 'Jobs' : name==='revenue' ? 'Revenue' : 'Car Spend']}
                  contentStyle={{ fontSize:12, borderRadius:8, border:'1px solid #e5e7eb' }} />
                {toggle==='revenue' && <Line type="monotone" dataKey="revenue" stroke={BL} strokeWidth={2.5} dot={false} name="revenue" />}
                {toggle==='car_spend' && <Line type="monotone" dataKey="car_spend" stroke={YW} strokeWidth={2.5} dot={false} name="car_spend" />}
                {toggle==='jobs' && <Line type="monotone" dataKey="jobs" stroke={GR} strokeWidth={2} dot={false} name="jobs" />}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex flex-col items-center justify-center text-gray-300">
              <TrendingUp className="w-12 h-12 mb-3 text-gray-200" />
              <p className="font-medium text-gray-400">Chart will populate as bookings come in</p>
              <p className="text-xs text-gray-300 mt-1">Data refreshes every 30 seconds</p>
            </div>
          )}
        </div>

        {/* Quick Summary */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h2 className="font-bold text-gray-900 mb-1">Business Summary</h2>
          <p className="text-xs text-gray-400 mb-4">All time totals</p>
          <div className="space-y-0">
            {[
              ['Total Jobs',      chart?.summary?.total_jobs   ?? stats?.total_bookings ?? 0,    BL],
              ['Completed',       chart?.summary?.completed    ?? stats?.completed      ?? 0,    GR],
              ['Pending',         chart?.summary?.pending      ?? stats?.pending        ?? 0,    '#f59e0b'],
              ['Invoiced Value',  `$${(chart?.summary?.invoiced_value??0).toLocaleString()}`,    '#7c3aed'],
              ['B2B Clients',     `${chart?.summary?.b2b_clients??0} active`,                   '#0891b2'],
              ['Cars Purchased',  chart?.summary?.car_purchases ?? stats?.car_purchases ?? 0,   '#6366f1'],
            ].map(([l, v, c]) => (
              <div key={l} className="flex justify-between items-center py-3 border-b border-gray-50 last:border-0">
                <span className="text-sm text-gray-600">{l}</span>
                <span className="font-black text-base" style={{ color: c }}>{v}</span>
              </div>
            ))}
          </div>
          <button onClick={() => setShowFin(true)}
            className="mt-4 w-full py-2.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 hover:opacity-90"
            style={{ backgroundColor: BL }}>
            <DollarSign className="w-4 h-4" /> Full Financial Report
          </button>
        </div>
      </div>

      {/* ── Top 5 Bar Charts ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <HBarChart title="🏙️ Top Pickup Suburbs"     data={chart?.top_suburbs}   color={BL}       emptyMsg="Jobs needed to show areas" />
        <HBarChart title="🏢 Top B2B Job Areas"       data={chart?.top_b2b_areas} color={GR}       emptyMsg="B2B jobs will appear here" />
        <HBarChart title="🚗 Top Car Makes Purchased" data={chart?.top_makes}     color="#f59e0b"  emptyMsg="Car purchases show here" />
      </div>

      {/* ── Recent Bookings ── */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-gray-900">Recent Bookings</h2>
            <p className="text-xs text-gray-400 mt-0.5">Last 10 jobs across all types</p>
          </div>
          <Link to="/admin/bookings" className="flex items-center gap-1.5 text-sm font-bold hover:opacity-80" style={{ color: BL }}>
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="bg-gray-50">
                {['Customer','Service','Vehicle','Date','Status','Charge'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recent.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12">
                  <Calendar className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                  <p className="text-gray-400 font-medium">No bookings yet</p>
                  <p className="text-gray-300 text-xs mt-1">They'll appear here as they come in</p>
                </td></tr>
              )}
              {recent.map(b => (
                <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3.5">
                    <p className="font-bold text-gray-900">{b.customer_name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{b.customer_phone}</p>
                  </td>
                  <td className="px-4 py-3.5">
                    <p className="text-sm font-medium text-gray-800">{b.service_type}</p>
                  </td>
                  <td className="px-4 py-3.5 text-sm">
                    {b.vehicle_make
                      ? <><p className="font-medium text-gray-800">{b.vehicle_year} {b.vehicle_make} {b.vehicle_model}</p><p className="text-xs text-gray-400">{b.vehicle_rego}</p></>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3.5">
                    <p className="text-sm font-medium text-gray-800">{b.scheduled_date || 'TBC'}</p>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${SC[b.status]||'bg-gray-100 text-gray-600'}`}>{b.status}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <p className="font-black text-base" style={{ color: BL }}>{b.charge ? `$${Number(b.charge).toLocaleString()}` : <span className="text-gray-400 font-normal text-sm">Free</span>}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}